import { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import { Plus, Clock, MapPin, Users, Trash2, ChevronLeft, ChevronRight, X, Download, Tag, Calendar, Shuffle, Sun, Moon, CalendarRange, Edit, Save, CheckSquare, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

/* ─── CONSTANTES ─── */
const TYPES = [
  { value: 'pratique', label: 'Pratique', color: 'bg-blue-100 text-blue-700 border-blue-200', dot: 'bg-blue-500' },
  { value: 'theorie', label: 'Théorie', color: 'bg-purple-100 text-purple-700 border-purple-200', dot: 'bg-purple-500' },
  { value: 'stage_structure', label: 'Stage en structure', color: 'bg-amber-100 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
  { value: 'foad', label: 'FOAD', color: 'bg-cyan-100 text-cyan-700 border-cyan-200', dot: 'bg-cyan-500' },
  { value: 'certification', label: 'Certification', color: 'bg-rose-100 text-rose-700 border-rose-200', dot: 'bg-rose-500' },
  { value: 'epmsp', label: 'EPMSP', color: 'bg-indigo-100 text-indigo-700 border-indigo-200', dot: 'bg-indigo-500' },
  { value: 'prerequis', label: 'Prérequis', color: 'bg-orange-100 text-orange-700 border-orange-200', dot: 'bg-orange-500' },
  { value: 'rattrapage', label: 'Rattrapage', color: 'bg-red-100 text-red-700 border-red-200', dot: 'bg-red-500' },
];

const RATTACHEMENTS = [
  { value: '', label: '-- Aucun --', color: '', short: '' },
  { value: 'bc1', label: 'BC1', color: 'bg-emerald-600 text-white', short: 'BC1' },
  { value: 'bc2', label: 'BC2', color: 'bg-teal-600 text-white', short: 'BC2' },
  { value: 'bc3', label: 'BC3', color: 'bg-green-600 text-white', short: 'BC3' },
];
const RATT_ACTIFS = RATTACHEMENTS.filter(r => r.value);

const SUPPORTS = ['VNM', 'JET À BRAS', 'BATEAU', 'PNM', 'ESH', 'ENGINS TRACTÉS'];
const LIEUX = ['Centre FISL', 'Lieu de pratique', 'Stage entreprise', 'Autre'];

const JOURS_SEM = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const JOURS_FULL = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
const MOIS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

const fmtKey = (d) => d.toISOString().split('T')[0];
const calcH = (a, b) => {
  if (!a || !b) return 0;
  const [h1, m1] = a.split(':').map(Number);
  const [h2, m2] = b.split(':').map(Number);
  return Math.max(0, (h2 * 60 + m2 - h1 * 60 - m1) / 60);
};

const getWeekDates = (date) => {
  const d = new Date(date); const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const mon = new Date(d.setDate(diff));
  return Array.from({ length: 7 }, (_, i) => { const nd = new Date(mon); nd.setDate(mon.getDate() + i); return nd; });
};

const getMonthDates = (date) => {
  const y = date.getFullYear(), m = date.getMonth();
  const first = new Date(y, m, 1), last = new Date(y, m + 1, 0);
  const startDay = first.getDay() === 0 ? 6 : first.getDay() - 1;
  const days = [];
  for (let i = -startDay; i <= last.getDate() - 1 + (6 - (last.getDay() === 0 ? 6 : last.getDay() - 1)); i++)
    days.push(new Date(y, m, i + 1));
  return days;
};

const getTypeConf = (v) => TYPES.find(t => t.value === v) || TYPES[0];
const getRattConf = (v) => RATTACHEMENTS.find(r => r.value === v);

const RattBadge = ({ val }) => {
  const r = getRattConf(val);
  if (!r?.value) return null;
  return <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${r.color}`}>{r.short}</span>;
};

/* ─── COMPOSANT PRINCIPAL ─── */
const AgendaFormation = () => {
  const [sequences, setSequences] = useState([]);
  const [dayHours, setDayHours] = useState({});
  const [stagiaires, setStagiaires] = useState([]);
  const [formateurs, setFormateurs] = useState([]);

  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('semaine');
  const [planningDate, setPlanningDate] = useState(null);
  const [showStageForm, setShowStageForm] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [journeesEntieres, setJourneesEntieres] = useState({});
  const [editingSeq, setEditingSeq] = useState(null);
  const [googleEvents, setGoogleEvents] = useState([]);

  // Charger données depuis l'API
  useEffect(() => {
    const load = async () => {
      try {
        const [stagRes, frmRes, seqRes, dhRes, gEvRes] = await Promise.all([
          axios.get('/stagiaires').catch(() => ({ data: [] })),
          axios.get('/formateurs').catch(() => ({ data: [] })),
          axios.get('/planning/sequences').catch(() => ({ data: [] })),
          axios.get('/planning/day-hours').catch(() => ({ data: {} })),
          axios.get('/planning/google-events').catch(() => ({ data: [] })),
        ]);
        const stags = (stagRes.data || []).map(s => ({
          id: s.user_id,
          nom: `${s.user_info?.prenom || s.prenom || ''} ${s.user_info?.nom || s.nom || ''}`.trim()
        }));
        setStagiaires(stags);
        setFormateurs((frmRes.data || []).map(f => ({
          id: f.user_id,
          nom: `${f.prenom || ''} ${f.nom || ''}`.trim()
        })));
        setSequences(seqRes.data || []);
        setDayHours(dhRes.data || {});
        setGoogleEvents(gEvRes.data || []);
      } catch (e) { console.error('Erreur chargement planning:', e); }
    };
    load();
  }, []);

  const weekDates = getWeekDates(currentDate);
  const monthDates = getMonthDates(currentDate);

  /* ─── Helpers ─── */
  const getDayHours = (date) => dayHours[date] || { matin: { debut: '09:00', fin: '13:00' }, apresmidi: { debut: '14:00', fin: '18:00' } };
  const getSeqsForDay = (date) => sequences.filter(s => s.date === date);
  const getSeqsForBloc = (date, bloc) => sequences.filter(s => s.date === date && s.bloc === bloc);

  const getGoogleEventsForDay = (date) => googleEvents.filter(e => {
    if (e.start_date === date) return true;
    // Multi-day events
    if (e.end_date && e.start_date <= date && e.end_date > date) return true;
    return false;
  });

  const handleImportGoogleEvent = (date, event) => {
    setSelectedDay(date);
    // Store event info to pre-fill the form
    setGoogleEventToImport(event);
  };

  const [googleEventToImport, setGoogleEventToImport] = useState(null);

  const getBusyStagiaires = (date, bloc, excludeId) => {
    const seqs = sequences.filter(s => s.date === date && s.bloc === bloc && s.id !== excludeId);
    return new Set(seqs.flatMap(s => s.stagiaires || []));
  };

  const setDayHoursForDate = (date, bloc, field, value) => {
    const newHours = {
      ...getDayHours(date),
      [bloc]: { ...(dayHours[date]?.[bloc] || getDayHours(date)[bloc]), [field]: value }
    };
    setDayHours(prev => ({ ...prev, [date]: newHours }));
    axios.post('/planning/day-hours', { date, hours: newHours }).catch(() => {});
  };

  const getSeqHeures = (seq) => {
    const dh = getDayHours(seq.date);
    const bloc = dh[seq.bloc];
    return calcH(bloc.debut, bloc.fin);
  };

  const addSequence = async (date, bloc, data) => {
    try {
      const payload = { date, bloc, ...data };
      const res = await axios.post('/planning/sequences', payload);
      payload.id = res.data.id;
      setSequences(prev => [...prev, payload]);
      toast.success('Séquence ajoutée');
    } catch { toast.error('Erreur sauvegarde'); }
  };

  const deleteSequence = async (id) => {
    try {
      await axios.delete(`/planning/sequences/${id}`);
      setSequences(prev => prev.filter(s => s.id !== id));
    } catch { toast.error('Erreur suppression'); }
  };

  const updateSequence = async (id, updates) => {
    try {
      await axios.put(`/planning/sequences/${id}`, updates);
      setSequences(prev => prev.map(s => s.id === id ? { ...s, ...updates } : s));
      toast.success('Séquence mise à jour');
    } catch { toast.error('Erreur mise à jour'); }
  };

  const updateSequenceFormateur = (id, formateur_id) => {
    const frm = formateurs.find(f => f.id === formateur_id);
    updateSequence(id, { formateur_id, formateur_nom: frm?.nom || '' });
  };

  /* ─── Stage période ─── */
  const [stageForm, setStageForm] = useState({ dateDebut: '', dateFin: '', theme: '', rattachement: '', formateur_id: '', lieu: 'Stage entreprise', demiJournee: false });

  const addStagePeriode = () => {
    if (!stageForm.dateDebut || !stageForm.dateFin) { toast.error('Dates obligatoires'); return; }
    if (!stageForm.theme) { toast.error('Thème obligatoire'); return; }
    const start = new Date(stageForm.dateDebut + 'T12:00');
    const end = new Date(stageForm.dateFin + 'T12:00');
    if (end < start) { toast.error('La date de fin doit être après la date de début'); return; }
    let count = 0;
    const d = new Date(start);
    while (d <= end) {
      const dateKey = fmtKey(d);
      const blocs = stageForm.demiJournee ? ['matin'] : ['matin', 'apresmidi'];
      blocs.forEach(bloc => {
        const frm = formateurs.find(f => f.id === stageForm.formateur_id);
        addSequence(dateKey, bloc, {
          type: 'stage_structure',
          rattachement: stageForm.rattachement, theme: stageForm.theme,
          support: '', formateur_id: stageForm.formateur_id || '', formateur_nom: frm?.nom || '',
          stagiaires: stagiaires.map(s => s.id),
          lieu: stageForm.lieu, notes: ''
        });
      });
      count++;
      d.setDate(d.getDate() + 1);
    }
    toast.success(`${count} jour(s) de stage ajoutés (${stageForm.demiJournee ? 'demi-journées' : 'journées complètes'})`);
    setStageForm({ dateDebut: '', dateFin: '', theme: '', rattachement: '', formateur_id: '', lieu: 'Stage entreprise', demiJournee: false });
    setShowStageForm(false);
  };

  /* ─── Stats ─── */
  const allWithHours = useMemo(() => sequences.map(s => ({ ...s, heures: getSeqHeures(s) })), [sequences, dayHours]);
  const totalHeures = allWithHours.reduce((a, s) => a + s.heures, 0);
  const statsByType = TYPES.map(t => ({ ...t, heures: allWithHours.filter(s => s.type === t.value).reduce((a, s) => a + s.heures, 0), count: allWithHours.filter(s => s.type === t.value).length }));
  const statsByRatt = RATT_ACTIFS.map(r => ({ ...r, heures: allWithHours.filter(s => s.rattachement === r.value).reduce((a, s) => a + s.heures, 0), count: allWithHours.filter(s => s.rattachement === r.value).length }));
  const formateursH = allWithHours.reduce((acc, s) => { const n = s.formateur_nom || ''; if (n) acc[n] = (acc[n] || 0) + s.heures; return acc; }, {});

  /* ─── Export CSV ─── */
  const exportCSV = () => {
    const headers = ['Date', 'Bloc', 'Début', 'Fin', 'Heures', 'Type', 'Rattachement', 'Thème', 'Support', 'Formateur', 'Stagiaires', 'Lieu'];
    const rows = allWithHours.sort((a, b) => a.date.localeCompare(b.date) || a.bloc.localeCompare(b.bloc))
      .map(s => {
        const dh = getDayHours(s.date)[s.bloc];
        return [
          new Date(s.date + 'T12:00').toLocaleDateString('fr-FR'), s.bloc === 'matin' ? 'Matin' : 'Après-midi',
          dh.debut, dh.fin, s.heures.toFixed(1), getTypeConf(s.type).label,
          getRattConf(s.rattachement)?.label || '', s.theme, s.support || '', s.formateur_nom || '',
          (s.stagiaires || []).map(id => stagiaires.find(st => st.id === id)?.nom || id).join(', '), s.lieu || ''
        ];
      });
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(';')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `agenda_formation_${fmtKey(new Date())}.csv`; a.click();
    toast.success('Export CSV téléchargé');
  };

  /* ─── Navigation ─── */
  const navDir = (dir) => {
    const d = new Date(currentDate);
    if (view === 'jour') d.setDate(d.getDate() + dir);
    else if (view === 'semaine') d.setDate(d.getDate() + dir * 7);
    else if (view === 'mois') d.setMonth(d.getMonth() + dir);
    setCurrentDate(d);
  };

  const getNavLabel = () => {
    if (view === 'jour') return currentDate.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    if (view === 'semaine') return `Sem. du ${weekDates[0].toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} au ${weekDates[6].toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}`;
    if (view === 'mois') return `${MOIS[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    return 'Session complète';
  };

  /* ─── MINI FORM pour ajouter/modifier séquence dans un bloc ─── */
  const SequenceForm = ({ date, bloc, onAdd, onClose, initial, googleEvent }) => {
    const defaultData = initial || {
      type: 'pratique', rattachement: '', theme: '', support: '', formateur_id: '', formateur_nom: '', stagiaires: [], lieu: 'Centre FISL', notes: '',
      ...(googleEvent ? { theme: googleEvent.summary, lieu: googleEvent.location || 'Centre FISL', notes: googleEvent.description || '', google_uid: googleEvent.uid } : {})
    };
    const [f, setF] = useState(defaultData);
    const busy = getBusyStagiaires(date, bloc, initial?.id || null);

    const toggleStagiaire = (id) => {
      setF(prev => ({
        ...prev,
        stagiaires: prev.stagiaires.includes(id) ? prev.stagiaires.filter(s => s !== id) : [...prev.stagiaires, id]
      }));
    };

    const selectAll = () => {
      const available = stagiaires.filter(s => !busy.has(s.id)).map(s => s.id);
      setF(prev => ({ ...prev, stagiaires: available }));
    };

    const selectNone = () => setF(prev => ({ ...prev, stagiaires: [] }));

    const randomize = () => {
      const available = stagiaires.filter(s => !busy.has(s.id)).map(s => s.id);
      const half = Math.ceil(available.length / 2);
      const shuffled = [...available].sort(() => Math.random() - 0.5);
      setF(prev => ({ ...prev, stagiaires: shuffled.slice(0, half) }));
    };

    const submit = () => {
      if (!f.theme) { toast.error('Thème obligatoire'); return; }
      onAdd(f);
      onClose();
    };

    return (
      <div className="border border-slate-200 rounded-lg p-3 bg-slate-50 space-y-3 mt-2">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <div>
            <label className="text-xs font-medium text-slate-600">Type</label>
            <select value={f.type} onChange={e => setF({...f, type: e.target.value})} className="w-full h-8 px-2 text-xs border border-slate-200 rounded" data-testid={`seq-type-${bloc}`}>
              {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Rattachement</label>
            <select value={f.rattachement} onChange={e => setF({...f, rattachement: e.target.value})} className="w-full h-8 px-2 text-xs border border-slate-200 rounded" data-testid={`seq-ratt-${bloc}`}>
              {RATTACHEMENTS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
          {f.type === 'pratique' && (
            <div>
              <label className="text-xs font-medium text-slate-600">Support</label>
              <select value={f.support} onChange={e => setF({...f, support: e.target.value})} className="w-full h-8 px-2 text-xs border border-slate-200 rounded">
                <option value="">--</option>
                {SUPPORTS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-slate-600">Formateur <span className="text-slate-400">(optionnel)</span></label>
            <select value={f.formateur_id} onChange={e => {
              const frm = formateurs.find(fr => fr.id === e.target.value);
              setF({...f, formateur_id: e.target.value, formateur_nom: frm?.nom || ''});
            }} className="w-full h-8 px-2 text-xs border border-slate-200 rounded" data-testid={`seq-formateur-${bloc}`}>
              <option value="">Assigner plus tard...</option>
              {formateurs.map(fr => <option key={fr.id} value={fr.id}>{fr.nom}</option>)}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <div>
            <label className="text-xs font-medium text-slate-600">Thème *</label>
            <input type="text" value={f.theme} onChange={e => setF({...f, theme: e.target.value})} placeholder="Ex: Navigation côtière..." className="w-full h-8 px-2 text-xs border border-slate-200 rounded" data-testid={`seq-theme-${bloc}`} />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Lieu</label>
            <select value={f.lieu} onChange={e => setF({...f, lieu: e.target.value})} className="w-full h-8 px-2 text-xs border border-slate-200 rounded">
              {LIEUX.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
        </div>

        {/* Stagiaires */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs font-medium text-slate-600">Stagiaires ({f.stagiaires.length}/{stagiaires.length})</label>
            <div className="flex items-center gap-2">
              <button type="button" onClick={selectAll} className="text-xs text-blue-600 hover:underline flex items-center gap-1" data-testid="select-all-stagiaires">
                <CheckSquare className="h-3 w-3" />Tous
              </button>
              <button type="button" onClick={selectNone} className="text-xs text-slate-400 hover:underline">Aucun</button>
              <button type="button" onClick={randomize} className="text-xs text-[#8DC63F] hover:underline flex items-center gap-1">
                <Shuffle className="h-3 w-3" />Aléatoire
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {stagiaires.map(st => {
              const isBusy = busy.has(st.id);
              const isSelected = f.stagiaires.includes(st.id);
              return (
                <button key={st.id} type="button"
                  disabled={isBusy}
                  onClick={() => toggleStagiaire(st.id)}
                  className={`px-2 py-1 rounded text-xs border transition-colors ${
                    isBusy ? 'bg-slate-100 text-slate-300 border-slate-100 cursor-not-allowed line-through' :
                    isSelected ? 'bg-[#8DC63F] text-white border-[#8DC63F]' :
                    'bg-white text-slate-600 border-slate-200 hover:border-[#8DC63F]'
                  }`}
                  title={isBusy ? 'Déjà dans un cours sur ce créneau' : ''}
                >
                  {st.nom}
                </button>
              );
            })}
            {stagiaires.length === 0 && <span className="text-xs text-slate-400 italic">Aucun stagiaire inscrit</span>}
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} className="text-xs">Annuler</Button>
          <Button size="sm" onClick={submit} className="bg-[#8DC63F] hover:bg-[#7AB62F] text-white text-xs" data-testid={`seq-submit-${bloc}`}>
            {initial ? <><Save className="h-3 w-3 mr-1" />Enregistrer</> : <><Plus className="h-3 w-3 mr-1" />Ajouter</>}
          </Button>
        </div>
      </div>
    );
  };

  /* ─── BLOC (Matin ou Après-midi) ─── */
  const BlocSection = ({ date, bloc, label, icon: Icon }) => {
    const [adding, setAdding] = useState(false);
    const dh = getDayHours(date)[bloc];
    const seqs = getSeqsForBloc(date, bloc);
    const blocH = calcH(dh.debut, dh.fin);

    return (
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <div className={`px-4 py-2 flex items-center justify-between ${bloc === 'matin' ? 'bg-amber-50' : 'bg-indigo-50'}`}>
          <div className="flex items-center gap-2">
            <Icon className={`h-4 w-4 ${bloc === 'matin' ? 'text-amber-500' : 'text-indigo-500'}`} />
            <span className="text-sm font-semibold text-slate-700">{label}</span>
          </div>
          <div className="flex items-center gap-2">
            <input type="time" value={dh.debut} onChange={e => setDayHoursForDate(date, bloc, 'debut', e.target.value)} className="h-7 px-2 text-xs border border-slate-200 rounded w-[90px]" data-testid={`${bloc}-debut`} />
            <span className="text-xs text-slate-400">→</span>
            <input type="time" value={dh.fin} onChange={e => setDayHoursForDate(date, bloc, 'fin', e.target.value)} className="h-7 px-2 text-xs border border-slate-200 rounded w-[90px]" data-testid={`${bloc}-fin`} />
            <span className={`text-xs font-bold px-2 py-0.5 rounded ${bloc === 'matin' ? 'bg-amber-100 text-amber-700' : 'bg-indigo-100 text-indigo-700'}`}>
              {blocH.toFixed(1)}h
            </span>
          </div>
        </div>

        <div className="p-3 space-y-2">
          {seqs.length === 0 && !adding && editingSeq !== `${date}-${bloc}` && (
            <p className="text-xs text-slate-400 text-center py-3">Aucune séquence</p>
          )}
          {seqs.map(seq => {
            const tc = getTypeConf(seq.type);
            const staNames = (seq.stagiaires || []).map(id => stagiaires.find(s => s.id === id)?.nom).filter(Boolean);
            const isEditing = editingSeq === seq.id;

            if (isEditing) {
              return (
                <SequenceForm key={seq.id} date={date} bloc={bloc}
                  initial={seq}
                  onAdd={(data) => { updateSequence(seq.id, data); setEditingSeq(null); }}
                  onClose={() => setEditingSeq(null)} />
              );
            }

            return (
              <div key={seq.id} className={`p-2.5 rounded-lg border ${tc.color} group relative`}>
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium border ${tc.color}`}>{tc.label}</span>
                  <RattBadge val={seq.rattachement} />
                  {seq.support && <span className="text-[10px] bg-white/50 px-1 py-0.5 rounded">{seq.support}</span>}
                </div>
                <p className="text-sm font-semibold">
                  {seq.foad_course_id ? (
                    <a href={`/foad`} onClick={(e) => { e.stopPropagation(); window.location.href = '/foad'; }}
                      className="hover:underline cursor-pointer" title="Aller au cours FOAD"
                      data-testid={`agenda-foad-link-${seq.id}`}>
                      {seq.theme}
                    </a>
                  ) : seq.theme}
                </p>
                {seq.foad_course_id && seq.date_fin_foad && (
                  <p className="text-[10px] text-cyan-600 mt-0.5">Fin FOAD: {new Date(seq.date_fin_foad).toLocaleDateString('fr-FR')}</p>
                )}
                <div className="flex flex-wrap gap-2 mt-1 text-xs opacity-75">
                  {seq.formateur_nom ? (
                    <span className="flex items-center gap-1"><Users className="h-3 w-3" />{seq.formateur_nom}</span>
                  ) : (
                    <span className="flex items-center gap-1 italic text-slate-400">
                      <Users className="h-3 w-3" />
                      <select className="bg-transparent text-xs border-b border-dashed border-slate-300 focus:outline-none focus:border-[#8DC63F]"
                        onChange={e => { if (e.target.value) updateSequenceFormateur(seq.id, e.target.value); }}
                        defaultValue="">
                        <option value="">Assigner formateur...</option>
                        {formateurs.map(fr => <option key={fr.id} value={fr.id}>{fr.nom}</option>)}
                      </select>
                    </span>
                  )}
                  {seq.lieu && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{seq.lieu}</span>}
                </div>
                {staNames.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {staNames.map(n => <span key={n} className="text-[10px] bg-white/60 px-1.5 py-0.5 rounded border border-white/30">{n}</span>)}
                  </div>
                )}
                {staNames.length === 0 && (
                  <p className="text-[10px] text-slate-400 italic mt-1">Aucun stagiaire assigné</p>
                )}
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 flex items-center gap-1 transition-opacity">
                  <button onClick={() => setEditingSeq(seq.id)} className="text-blue-500 hover:text-blue-700" title="Modifier" data-testid={`edit-seq-${seq.id}`}>
                    <Edit className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => deleteSequence(seq.id)} className="text-red-400 hover:text-red-600" title="Supprimer">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
          {adding ? (
            <SequenceForm date={date} bloc={bloc} onAdd={(data) => { addSequence(date, bloc, data); setGoogleEventToImport(null); }} onClose={() => { setAdding(false); setGoogleEventToImport(null); }} googleEvent={googleEventToImport} />
          ) : (
            <div className="space-y-1">
              {/* Show Google event import button if one is selected for this bloc */}
              {googleEventToImport && (
                <button onClick={() => setAdding(true)} className="w-full py-2 border border-dashed border-blue-400 bg-blue-50 rounded-lg text-xs text-blue-600 hover:border-blue-500 hover:bg-blue-100 transition-colors flex items-center justify-center gap-1" data-testid={`import-google-${bloc}`}>
                  <Calendar className="h-3 w-3" />Importer "{googleEventToImport.summary.substring(0, 40)}" ici
                </button>
              )}
              <button onClick={() => { setGoogleEventToImport(null); setAdding(true); }} className="w-full py-2 border border-dashed border-slate-300 rounded-lg text-xs text-slate-500 hover:border-[#8DC63F] hover:text-[#8DC63F] transition-colors flex items-center justify-center gap-1" data-testid={`add-seq-${bloc}`}>
                <Plus className="h-3 w-3" />Ajouter une séquence
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  /* ─── VUE PLANIFICATION JOURNÉE ─── */
  const DayPlanner = ({ date, onClose }) => {
    const journeeEntiere = !!journeesEntieres[date];
    const dayDate = new Date(date + 'T12:00');
    const seqs = getSeqsForDay(date);
    const gEvents = getGoogleEventsForDay(date).filter(ge => !seqs.some(s => s.google_uid === ge.uid));

    const toggleJournee = () => {
      const next = !journeeEntiere;
      setJourneesEntieres(prev => ({ ...prev, [date]: next }));
      if (next) {
        setDayHoursForDate(date, 'matin', 'debut', '09:00');
        setDayHoursForDate(date, 'matin', 'fin', '17:00');
      } else {
        setDayHoursForDate(date, 'matin', 'debut', '09:00');
        setDayHoursForDate(date, 'matin', 'fin', '13:00');
        setDayHoursForDate(date, 'apresmidi', 'debut', '14:00');
        setDayHoursForDate(date, 'apresmidi', 'fin', '18:00');
      }
    };

    return (
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-[#0F172A]">
              {JOURS_FULL[dayDate.getDay() === 0 ? 6 : dayDate.getDay() - 1]} {dayDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
            </h3>
            <p className="text-xs text-slate-500">{seqs.length} séquence(s)</p>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="journee-entiere-toggle">
              <input type="checkbox" checked={journeeEntiere} onChange={toggleJournee}
                className="h-4 w-4 rounded border-slate-300 text-[#8DC63F] focus:ring-[#8DC63F]" />
              <span className="text-sm font-medium text-slate-700">Journée entière</span>
            </label>
            {onClose && <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>}
          </div>
        </div>
        <div className="p-4 space-y-4">
          {/* Google events to import */}
          {gEvents.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs font-semibold text-blue-700 mb-2 flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> Evenements Google Calendar ({gEvents.length})</p>
              <div className="space-y-1.5">
                {gEvents.map(ge => (
                  <div key={ge.uid} className="flex items-center justify-between bg-white p-2 rounded border border-blue-100">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-800 truncate">{ge.summary}</p>
                      {ge.location && <p className="text-xs text-slate-500 truncate">{ge.location}</p>}
                    </div>
                    <button onClick={() => setGoogleEventToImport(ge)}
                      className="ml-2 px-2.5 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 flex-shrink-0 flex items-center gap-1" data-testid={`import-gevent-${ge.uid}`}>
                      <Plus className="h-3 w-3" />Importer
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {journeeEntiere ? (
            <BlocSection date={date} bloc="matin" label="Journée entière (8h)" icon={Calendar} />
          ) : (
            <>
              <BlocSection date={date} bloc="matin" label="Matin" icon={Sun} />
              <BlocSection date={date} bloc="apresmidi" label="Après-midi" icon={Moon} />
            </>
          )}
        </div>
      </div>
    );
  };

  /* ─── Séquence card compact (pour calendrier) ─── */
  const SeqCard = ({ s }) => {
    const tc = getTypeConf(s.type);
    return (
      <div className={`p-1 rounded text-[10px] border ${tc.color} mb-0.5`}>
        <div className="flex items-center gap-0.5">
          <span className="font-semibold">{s.bloc === 'matin' ? 'AM' : 'PM'}</span>
          <RattBadge val={s.rattachement} />
        </div>
        <p className="truncate font-medium">{s.theme}</p>
        {s.formateur && <p className="truncate opacity-70">{s.formateur}</p>}
      </div>
    );
  };

  /* ─── Séquences groupées par date ─── */
  const seqsByDate = sequences.reduce((acc, s) => { if (!acc[s.date]) acc[s.date] = []; acc[s.date].push(s); return acc; }, {});

  /* ─── RENDER ─── */
  return (
    <div className="space-y-4" data-testid="agenda-formation">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-4xl font-display font-bold text-[#0F172A]">Agenda Formation</h1>
          <p className="text-slate-600 text-sm">Planification des séances de formation</p>
        </div>
        <div className="flex items-center gap-2 print:hidden">
          <Button variant="outline" size="sm" onClick={exportCSV} disabled={sequences.length === 0} data-testid="export-csv-btn">
            <Download className="h-4 w-4 mr-1" />CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="print-btn">
            <Download className="h-4 w-4 mr-1" />Imprimer
          </Button>
        </div>
      </div>

      {/* KPIs par type */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <div className="bg-white border border-slate-200 rounded-lg p-3 border-l-4 border-l-slate-500">
          <p className="text-xs text-slate-500">Total</p>
          <p className="text-xl font-bold text-slate-700">{totalHeures.toFixed(1)}h</p>
          <p className="text-xs text-slate-400">{sequences.length} séquences</p>
        </div>
        {statsByType.map(st => (
          <div key={st.value} className="bg-white border border-slate-200 rounded-lg p-3 border-l-4"
            style={{ borderLeftColor: st.value === 'pratique' ? '#3b82f6' : st.value === 'theorie' ? '#8b5cf6' : st.value === 'stage_structure' ? '#f59e0b' : '#06b6d4' }}>
            <p className="text-xs text-slate-500">{st.label}</p>
            <p className="text-xl font-bold">{st.heures.toFixed(1)}h</p>
            <p className="text-xs text-slate-400">{st.count} séq.</p>
          </div>
        ))}
      </div>

      {/* KPIs par rattachement */}
      {sequences.some(s => s.rattachement) && (
        <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <Tag className="h-4 w-4 text-[#8DC63F]" />Cumul d'heures par rattachement
          </h3>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            {statsByRatt.map(r => (
              <div key={r.value} className="text-center p-3 rounded-lg border border-slate-100 bg-slate-50">
                <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold mb-1 ${r.color}`}>{r.label}</span>
                <p className="text-2xl font-bold text-slate-800">{r.heures.toFixed(1)}h</p>
                <p className="text-xs text-slate-400">{r.count} séq.</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Boutons d'action */}
      <div className="flex items-center gap-2 print:hidden">
        <Button variant="outline" size="sm" onClick={() => setShowStageForm(!showStageForm)} className={showStageForm ? 'border-amber-300 bg-amber-50' : ''} data-testid="stage-period-btn">
          <CalendarRange className="h-4 w-4 mr-1" />Période de stage
        </Button>
        <Button onClick={() => setPlanningDate(planningDate ? null : fmtKey(currentDate))} data-testid="plan-day-btn"
          className={`${planningDate ? 'bg-slate-600' : 'bg-[#8DC63F] hover:bg-[#7AB62F]'} text-white`}>
          {planningDate ? <><X className="h-4 w-4 mr-2" />Fermer</> : <><Plus className="h-4 w-4 mr-2" />Planifier une journée</>}
        </Button>
      </div>

      {/* Formulaire période de stage */}
      {showStageForm && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3" data-testid="stage-form">
          <h3 className="text-sm font-semibold text-amber-800 flex items-center gap-2"><CalendarRange className="h-4 w-4" />Ajouter une période de stage</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <div>
              <label className="text-xs font-medium text-slate-600">Du *</label>
              <input type="date" value={stageForm.dateDebut} onChange={e => setStageForm({...stageForm, dateDebut: e.target.value})} className="w-full h-8 px-2 text-xs border border-slate-200 rounded" data-testid="stage-date-debut" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Au *</label>
              <input type="date" value={stageForm.dateFin} onChange={e => setStageForm({...stageForm, dateFin: e.target.value})} className="w-full h-8 px-2 text-xs border border-slate-200 rounded" data-testid="stage-date-fin" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Thème *</label>
              <input type="text" value={stageForm.theme} onChange={e => setStageForm({...stageForm, theme: e.target.value})} placeholder="Stage en entreprise..." className="w-full h-8 px-2 text-xs border border-slate-200 rounded" data-testid="stage-theme" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Rattachement</label>
              <select value={stageForm.rattachement} onChange={e => setStageForm({...stageForm, rattachement: e.target.value})} className="w-full h-8 px-2 text-xs border border-slate-200 rounded">
                {RATTACHEMENTS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Formateur</label>
              <select value={stageForm.formateur_id} onChange={e => setStageForm({...stageForm, formateur_id: e.target.value})} className="w-full h-8 px-2 text-xs border border-slate-200 rounded">
                <option value="">Assigner plus tard...</option>
                {formateurs.map(fr => <option key={fr.id} value={fr.id}>{fr.nom}</option>)}
              </select>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
              <input type="checkbox" checked={stageForm.demiJournee} onChange={e => setStageForm({...stageForm, demiJournee: e.target.checked})} className="rounded" />
              Demi-journées uniquement (matin)
            </label>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setShowStageForm(false)} className="text-xs">Annuler</Button>
              <Button size="sm" onClick={addStagePeriode} className="bg-amber-500 hover:bg-amber-600 text-white text-xs" data-testid="stage-submit">
                <Plus className="h-3 w-3 mr-1" />Ajouter la période
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Planification journée */}
      {planningDate && (
        <div className="space-y-3 print:hidden">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-slate-700">Date :</label>
            <input type="date" value={planningDate} onChange={e => setPlanningDate(e.target.value)} className="h-9 px-3 text-sm border border-slate-200 rounded-md" data-testid="planning-date" />
          </div>
          <DayPlanner date={planningDate} onClose={() => setPlanningDate(null)} />
        </div>
      )}

      {/* Calendrier */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between p-3 border-b border-slate-200 gap-2">
          <div className="flex items-center gap-1 print:hidden">
            {['jour', 'semaine', 'mois', 'session'].map(v => (
              <button key={v} onClick={() => { setView(v); setSelectedDay(null); }}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${view === v ? 'bg-[#8DC63F] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                data-testid={`view-${v}`}>{v.charAt(0).toUpperCase() + v.slice(1)}</button>
            ))}
          </div>
          {view !== 'session' ? (
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navDir(-1)} data-testid="nav-prev"><ChevronLeft className="h-4 w-4" /></Button>
              <span className="text-sm font-semibold text-slate-700 min-w-[200px] text-center">{getNavLabel()}</span>
              <Button variant="ghost" size="sm" onClick={() => navDir(1)} data-testid="nav-next"><ChevronRight className="h-4 w-4" /></Button>
              <button onClick={() => setCurrentDate(new Date())} className="text-xs text-[#8DC63F] hover:underline print:hidden">Aujourd'hui</button>
            </div>
          ) : (
            <span className="text-sm font-semibold text-slate-700">Toutes les séquences</span>
          )}
        </div>

        {/* VUE JOUR */}
        {view === 'jour' && <div className="p-4"><DayPlanner date={fmtKey(currentDate)} /></div>}

        {/* VUE SEMAINE */}
        {view === 'semaine' && !selectedDay && (
          <div className="grid grid-cols-7 divide-x divide-slate-200">
            {weekDates.map((date, idx) => {
              const dk = fmtKey(date);
              const daySeqs = seqsByDate[dk] || [];
              const isToday = fmtKey(new Date()) === dk;
              return (
                <div key={idx} className={`min-h-[220px] cursor-pointer hover:bg-slate-50/50 transition-colors ${isToday ? 'bg-green-50/30' : ''}`}
                  onClick={() => setSelectedDay(dk)}>
                  <div className={`p-2 text-center border-b ${isToday ? 'bg-[#8DC63F]/10' : 'bg-slate-50'}`}>
                    <p className="text-xs text-slate-500">{JOURS_SEM[idx]}</p>
                    <p className={`text-lg font-bold ${isToday ? 'text-[#8DC63F]' : 'text-slate-700'}`}>{date.getDate()}</p>
                    {daySeqs.length > 0 && <p className="text-xs text-slate-400">{daySeqs.length} séq.</p>}
                  </div>
                  <div className="p-1">{daySeqs.slice(0, 4).map(s => <SeqCard key={s.id} s={s} />)}
                    {daySeqs.length > 4 && <p className="text-[10px] text-slate-400 text-center">+{daySeqs.length - 4}</p>}
                    {/* Google Calendar Events */}
                    {getGoogleEventsForDay(dk).filter(ge => !daySeqs.some(s => s.google_uid === ge.uid)).slice(0, 3).map(ge => (
                      <div key={ge.uid} onClick={(e) => { e.stopPropagation(); handleImportGoogleEvent(dk, ge); }}
                        className="px-1.5 py-0.5 rounded text-[10px] truncate border border-dashed border-blue-300 bg-blue-50/50 text-blue-600 mb-0.5 flex items-center gap-1 hover:bg-blue-100 cursor-pointer" title={`Google: ${ge.summary} - Cliquer pour importer`}>
                        <Calendar className="h-2.5 w-2.5 flex-shrink-0 text-blue-400" />{ge.summary}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {view === 'semaine' && selectedDay && <div className="p-4"><DayPlanner date={selectedDay} onClose={() => setSelectedDay(null)} /></div>}

        {/* VUE MOIS */}
        {view === 'mois' && !selectedDay && (
          <div>
            <div className="grid grid-cols-7 border-b border-slate-200">
              {JOURS_SEM.map(j => <div key={j} className="p-2 text-center text-xs font-medium text-slate-500 bg-slate-50">{j}</div>)}
            </div>
            <div className="grid grid-cols-7 divide-x divide-y divide-slate-100">
              {monthDates.map((date, idx) => {
                const dk = fmtKey(date);
                const daySeqs = seqsByDate[dk] || [];
                const isToday = fmtKey(new Date()) === dk;
                const isCurMonth = date.getMonth() === currentDate.getMonth();
                return (
                  <div key={idx} className={`min-h-[85px] p-1 cursor-pointer hover:bg-slate-50 ${!isCurMonth ? 'opacity-30' : ''} ${isToday ? 'bg-green-50/40' : ''}`}
                    onClick={() => setSelectedDay(dk)}>
                    <div className="flex items-center justify-between mb-0.5">
                      <span className={`text-xs font-medium ${isToday ? 'bg-[#8DC63F] text-white w-5 h-5 rounded-full flex items-center justify-center' : 'text-slate-600'}`}>{date.getDate()}</span>
                      {daySeqs.length > 0 && <span className="text-[10px] text-slate-400">{daySeqs.length}</span>}
                    </div>
                    {daySeqs.slice(0, 2).map(s => {
                      const tc = getTypeConf(s.type);
                      return <div key={s.id} className={`px-1 py-0.5 rounded text-[10px] truncate border ${tc.color} mb-0.5 flex items-center gap-0.5`}><RattBadge val={s.rattachement} />{s.theme}</div>;
                    })}
                    {daySeqs.length > 2 && <p className="text-[10px] text-slate-400 text-center">+{daySeqs.length - 2}</p>}
                    {/* Google Calendar Events */}
                    {getGoogleEventsForDay(dk).filter(ge => !daySeqs.some(s => s.google_uid === ge.uid)).slice(0, 1).map(ge => (
                      <div key={ge.uid} onClick={(e) => { e.stopPropagation(); handleImportGoogleEvent(dk, ge); }}
                        className="px-1 py-0.5 rounded text-[10px] truncate border border-dashed border-blue-300 bg-blue-50/50 text-blue-600 mb-0.5 flex items-center gap-0.5 hover:bg-blue-100 cursor-pointer" title={`Google: ${ge.summary}`}>
                        <Calendar className="h-2.5 w-2.5 flex-shrink-0 text-blue-400" />{ge.summary}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {view === 'mois' && selectedDay && <div className="p-4"><DayPlanner date={selectedDay} onClose={() => setSelectedDay(null)} /></div>}

        {/* VUE SESSION */}
        {view === 'session' && (
          <div className="p-4">
            {sequences.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-8">Aucune séquence enregistrée</p>
            ) : (
              <div className="space-y-3">
                {Object.entries(seqsByDate).sort(([a], [b]) => a.localeCompare(b)).map(([dk, daySeqs]) => (
                  <div key={dk} className="border border-slate-100 rounded-lg overflow-hidden">
                    <div className="bg-slate-50 px-4 py-2 flex items-center justify-between cursor-pointer hover:bg-slate-100" onClick={() => setSelectedDay(dk)}>
                      <span className="text-sm font-semibold text-slate-700">
                        {new Date(dk + 'T12:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                      </span>
                      <span className="text-xs text-slate-500">{daySeqs.length} séq.</span>
                    </div>
                    <div className="divide-y divide-slate-50">
                      {daySeqs.sort((a, b) => a.bloc.localeCompare(b.bloc)).map(s => {
                        const tc = getTypeConf(s.type);
                        const dh = getDayHours(s.date)[s.bloc];
                        return (
                          <div key={s.id} className="flex items-center gap-3 px-4 py-2 hover:bg-slate-50 group">
                            <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${s.bloc === 'matin' ? 'bg-amber-100 text-amber-700' : 'bg-indigo-100 text-indigo-700'}`}>{s.bloc === 'matin' ? 'AM' : 'PM'}</span>
                            <span className="text-xs font-mono text-slate-500 min-w-[90px]">{dh.debut} - {dh.fin}</span>
                            <span className={`px-1.5 py-0.5 rounded text-xs border ${tc.color} min-w-[60px] text-center`}>{tc.label}</span>
                            <RattBadge val={s.rattachement} />
                            <span className="text-sm font-medium flex-1 truncate">{s.theme}</span>
                            <span className="text-xs text-slate-500 min-w-[80px]">{s.formateur || <em className="text-slate-300">non assigné</em>}</span>
                            <span className="text-xs text-slate-400">{getSeqHeures(s).toFixed(1)}h</span>
                            <button onClick={() => deleteSequence(s.id)} className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 transition-opacity">
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Récap formateurs */}
      {Object.keys(formateursH).length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><Users className="h-4 w-4 text-[#8DC63F]" />Heures par formateur</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {Object.entries(formateursH).sort((a, b) => b[1] - a[1]).map(([nom, h]) => (
              <div key={nom} className="p-2.5 bg-slate-50 rounded border border-slate-100 flex items-center justify-between">
                <span className="text-sm text-slate-700">{nom}</span>
                <span className="text-sm font-bold text-[#8DC63F]">{h.toFixed(1)}h</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Légende */}
      <div className="flex flex-wrap gap-4">
        <div className="flex flex-wrap gap-3">
          <span className="text-xs text-slate-500 font-medium">Types:</span>
          {TYPES.map(t => <div key={t.value} className="flex items-center gap-1"><span className={`w-3 h-3 rounded-full ${t.dot}`}></span><span className="text-xs text-slate-600">{t.label}</span></div>)}
        </div>
        <div className="flex flex-wrap gap-2">
          <span className="text-xs text-slate-500 font-medium">Rattachements:</span>
          {RATT_ACTIFS.map(r => <span key={r.value} className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${r.color}`}>{r.label}</span>)}
        </div>
      </div>
    </div>
  );
};

export default AgendaFormation;
